/**
 * AprovacaoDasTelas — aprovar telas do Design Studio é ação real (decisão do dono, 2026-10-07).
 *
 * Até aqui nenhuma tela era gravada como "aprovada": o Picky V10 foi ao codegen com 82 de 82
 * telas em "proposta". Agora o dono aprova (todas de uma vez ou tela a tela) e a fábrica usa
 * só as aprovadas — quando há pelo menos uma. Sem nenhuma aprovada, usa todas e avisa.
 *
 *   - `ResumoDaAprovacao`: "N de M telas aprovadas", o que o codegen vai usar, "Aprovar todas".
 *   - `SeloDeAprovacao`: selo + botão por tela (aprovar / reabrir).
 *
 * Tela com layout cortado não é aprovada: a API devolve o motivo e ele aparece aqui.
 */
import { C } from '@/theme/palette';

export function contarAprovadas(screens) {
  return (screens || []).filter((s) => s && s.status === 'aprovada').length;
}

/** O que o codegen vai usar, dito em uma frase. */
export function fraseDoQueVale(aprovadas, total) {
  if (!total) return '';
  if (aprovadas === 0) {
    return 'Nenhuma tela aprovada: o codegen vai usar todas as telas, com aviso.';
  }
  if (aprovadas === total) return 'Todas as telas estão aprovadas e vão ao codegen.';
  const fora = (total - aprovadas).toLocaleString('pt-BR');
  return `O codegen vai usar só as aprovadas — ${fora} tela(s) ficam de fora.`;
}

/**
 * @param {{ screens: object[], onAprovarTodas: () => void, ocupado?: boolean,
 *           recusadas?: {name: string, motivo: string}[], erro?: string|null }} props
 */
export function ResumoDaAprovacao({ screens, onAprovarTodas, ocupado = false, recusadas = [],
  erro = null }) {
  const total = (screens || []).length;
  if (!total) return null;
  const aprovadas = contarAprovadas(screens);
  const tudo = aprovadas === total;
  return (
    <div data-testid="resumo-aprovacao" style={{ margin: '0 0 12px', padding: '10px 12px',
      borderRadius: 8, border: `1px solid ${tudo ? C.border : `${C.accent}66`}`, fontSize: 12.5,
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <strong style={{ color: C.text }}>
          {aprovadas.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} tela(s) aprovadas
        </strong>
        <div style={{ color: aprovadas === 0 ? C.warn : C.dim, marginTop: 2 }}>
          {fraseDoQueVale(aprovadas, total)}
        </div>
        {recusadas.length > 0 && (
          <ul role="alert" data-testid="aprovacao-recusadas"
              style={{ margin: '6px 0 0', paddingLeft: 18, color: C.warn }}>
            {recusadas.map((r) => <li key={r.name}>{r.name} — não aprovada: {r.motivo}</li>)}
          </ul>
        )}
        {erro && <div role="alert" style={{ marginTop: 6, color: C.digital }}>{String(erro)}</div>}
      </div>
      {!tudo && (
        <button type="button" onClick={onAprovarTodas} disabled={ocupado}
          aria-label="Aprovar todas as telas"
          style={{ cursor: ocupado ? 'default' : 'pointer', background: C.accent, color: C.black,
            border: 0, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
            opacity: ocupado ? 0.6 : 1 }}>
          {ocupado ? 'Aprovando…' : '✓ Aprovar todas'}
        </button>
      )}
    </div>
  );
}

/**
 * @param {{ screen: object, onMudar: (aprovar: boolean) => void, ocupado?: boolean }} props
 */
export function SeloDeAprovacao({ screen, onMudar, ocupado = false }) {
  const aprovada = screen?.status === 'aprovada';
  const cortada = Boolean(screen?.layout_cortado);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span data-testid="selo-aprovacao" style={{ fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999,
        color: aprovada ? C.black : C.dim, background: aprovada ? C.accent : 'transparent',
        border: `1px solid ${aprovada ? C.accent : C.border}` }}>
        {aprovada ? 'aprovada' : (screen?.status || 'proposta')}
      </span>
      <button type="button" disabled={ocupado || (!aprovada && cortada)}
        onClick={() => onMudar(!aprovada)}
        title={!aprovada && cortada ? 'Layout cortado: conserte antes de aprovar' : undefined}
        aria-label={`${aprovada ? 'Reabrir' : 'Aprovar'} a tela ${screen?.name}`}
        style={{ cursor: (ocupado || (!aprovada && cortada)) ? 'default' : 'pointer',
          background: 'transparent', color: aprovada ? C.dim : C.accent,
          border: `1px solid ${aprovada ? C.border : `${C.accent}66`}`, borderRadius: 6,
          padding: '3px 9px', fontSize: 11, opacity: (ocupado || (!aprovada && cortada)) ? 0.5 : 1 }}>
        {aprovada ? 'reabrir' : '✓ aprovar'}
      </button>
    </span>
  );
}
